import { useSyncExternalStore } from 'react';
import tinycolor from 'tinycolor2';

function subscribe(onStoreChange: () => void) {
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.attributeName === 'style') {
        onStoreChange();
      }
    }
  });
  observer.observe(document.documentElement, { attributes: true, attributeFilter: ['style'] });
  return () => observer.disconnect();
}

/**
 * Sets the base color CSS custom property on the document root element.
 * @param color - A color string that can be parsed by tinycolor (e.g., hex, rgb, hsl)
 * @example
 * setBaseColor('#ff0000');
 * setBaseColor('rgb(255, 0, 0)');
 */
export function setBaseColor(color: string) {
  const colorObj = tinycolor(color);
  document.documentElement.style.setProperty('--base-color', colorObj.toRgbString());
}

/**
 * Retrieves the base color from the CSS custom property and converts it to hexadecimal format.
 *
 * @returns {string} The base color as a hexadecimal string (e.g., 'ffffff').
 *
 * @example
 * const baseColor = getBaseColor();
 * console.log(baseColor); // '3498db'
 */
export function getBaseColor(): string {
  const color = getComputedStyle(document.documentElement).getPropertyValue('--base-color');
  return tinycolor(color).toHex();
}

/**
 * A React hook that provides reactive access to the base color CSS custom property.
 *
 * @returns A tuple with the current hex color and an update function
 *
 * @example
 * const [baseColor, setColor] = useBaseColor();
 * console.log(baseColor); // 'ff0000'
 * setColor('#00ff00');
 */
export function useBaseColor(): [string, (color: string) => void] {
  const color = useSyncExternalStore(subscribe, getBaseColor, getBaseColor);
  return [color, setBaseColor];
}
